import toast from 'react-hot-toast'
import crownIcon from '../../assets/crown.gif'
import { FC, useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { BoltIcon, ArrowRightIcon } from '@heroicons/react/20/solid'
import { Avatar, Header } from '../../components'
import { useGame, useMailman } from '../../utils'
import { GuessType, MapType, RoundType } from '../../types'
import { StyledHomeView } from '.'
import { RoundView } from '../RoundView'
import { useAppSelector } from '../../redux'

const HomeView: FC = () => {
  const { mailman } = useMailman()
  const { _id } = useAppSelector((state) => state.user)
  const [loading, setLoading] = useState(true)
  const [maps, setMaps] = useState<MapType[]>([])
  const [ongoingGame, setOngoingGame] = useState<RoundType[]>([])
  const [ongoingGuess, setOngoingGuess] = useState<GuessType>()
  const { guess, rounds, actions: { startGame, submitGuess, nextRound, setRounds, setGuess } } = useGame()

  const getOngoingGame = async () => {
    const response = await mailman('games/ongoing')

    if (!response) {
      return
    }

    setOngoingGame(response.rounds)
    
    const recentRound = response.rounds[response.rounds.length - 1]

    if (!recentRound.guess) {
      return
    }

    setOngoingGuess({
      guess: recentRound.guess,
      real: recentRound.real,
      score: recentRound.score
    })
  }

  const continueGame = () => {
    setRounds(ongoingGame)
    setGuess(ongoingGuess)
    setOngoingGame([])
  }

  const getMaps = async () => {
    const response = await mailman('maps')

    if (!response) {
      return
    }

    setMaps(response)
  }

  const loadData = async () => {
    try {
      setLoading(true)
      await getOngoingGame()
      await getMaps()
    }
    catch (err) {
      toast.error((err as any).message || 'Whoops! Something went wrong.')
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!!rounds.length || !_id) {
      return
    }

    loadData()
  }, [_id, rounds])

  if (rounds.length || !!guess) {
    return (
      <RoundView
        guess={guess}
        round={rounds[rounds.length - 1]}
        submitGuess={(amount) => submitGuess(amount)}
        nextRound={() => nextRound(rounds[0].game)}
      />
    )
  }

  return (
    <StyledHomeView>
      <Header/>
      <div className="page-container">
        {loading && <span>Loading...</span>}
        {!loading && (
          <>
            {!!ongoingGame?.length && (
              <div className="continue-game">
                <span>You have an unfinished game!</span>
                <button onClick={() => continueGame()}>Continue <ArrowRightIcon/></button>
              </div>
            )}
            <div className="map-list">
              {maps.map((map) => (
                <div className="map-group" key={map._id}>
                  <div className="map-header">
                    <div className="map-details-container">
                      <div className="map-icon">
                        <img src={map.avatar} alt={map.title}/>
                      </div>
                      <div className="map-details">
                        <span className="map-title">{map.title}</span>
                        <span className="map-homes">{map.homes} homes</span>
                      </div>
                    </div>
                    <button onClick={() => startGame(map._id)}>
                      {!ongoingGame.length && <><BoltIcon/> Play</>}
                      {!!ongoingGame.length && <><BoltIcon/> New Game</>}
                    </button>
                  </div>
                  <div className="map-games">
                    {map.games.map((game, index) => (
                      <div className="map-game" key={game._id}>
                        <div className="map-game-user-details">
                          <div className="map-game-user-avatar">
                            <Avatar emoji={game.user.avatar} size={30}/>
                          </div>
                          <div className="map-game-user-stats">
                            <span className="map-game-user-username">@{game.user.username} {index === 0 && <div className="crown"><img src={crownIcon} alt="crown"/></div>}</span>
                            <span className="map-game-user-stat">{DateTime.fromJSDate(new Date(game.finishedAt)).toRelative({ style: 'short' })?.replace('.', '')}</span>
                          </div>
                        </div>
                        <span className="map-game-user-score">{game.score}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </StyledHomeView>
  )
}

export default HomeView
